/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import { QueryClient, QueryClientProvider } from "react-query";
import { Provider } from "react-redux";
import {
    BrowserRouter,
    Route,
    Routes,
    useParams,
    useLocation,
    useNavigate,
} from "react-router-dom";
import { Slide, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.min.css";
import { applyMiddleware, createStore } from "redux";
import thunk from "redux-thunk";
import Home from "./components/home";
import NoMatch from "./components/no-match";
import { PrivateRoute } from "./components/PrivateRoute";
import { Solera } from "./components/solera";
import { BrandingContextProvider } from "./contexts/branding";
import { rootReducer } from "./reducers";
import { Register as CarePlanRegister } from "./ui/careplan/register";
import { LoadingContainer } from "./ui/components/loading-container";
import { NewFeedback } from "./ui/components/new-feedbacks/new-feedback";
import EnrollUser from "./ui/enroll-uesr";
import ErrorPage from "./ui/error-page";
import Feedback from "./ui/feedback/feedback";
import { JotForm } from "./ui/jot-forms";
import ExternalLoginHandler from "./ui/login/external-handler";
import LoginWithMercyCallback from "./ui/login/mercy/callback";
import LoginWithOhioUniversityCallback from "./ui/login/ohio-university/callback";
import LoginWithTrinityCallback from "./ui/login/trinity/callback";
import { CoachMeeting } from "./ui/meeting/coach-meeting";
import { MeetingErrorPage } from "./ui/meeting/pages/error";
import { RecordingPage } from "./ui/meeting/pages/recording-page";
import { RejoinPage } from "./ui/meeting/pages/rejoin";
import { ForgotPassword } from "./ui/password/forgot-password";
import { PasswordReset } from "./ui/password/password-reset";
import { Confirm as PowConfirm } from "./ui/pow/confirm";
import { Register as PowRegister } from "./ui/pow/register";
import { RegisterSuccess as PowSuccess } from "./ui/pow/register-success";
import Privacy from "./ui/privacy-terms/privacy";
import Terms from "./ui/privacy-terms/terms";
import ProjectPowerEnrollment from "./ui/project-power-enrollment";
import { RecipeDetailViewCard } from "./ui/recipe-book/custom/components/recipe-detail-view-card";
import { JotformSignUp } from "./ui/registration/jotform-sign-up";
import { Registered } from "./ui/registration/registered";
import { SignUp } from "./ui/registration/sign-up";
import IndiaPaymentPage from "./ui/india-payment";
import { SignUpContainer } from "./ui/registration/sign-up-container";
import { SoleraCallback } from "./ui/solera-callback";
import { Subscription } from "./ui/subscription-external";
import { UserSignupComponent } from "./ui/user-coupon-registration/signup";
import "./utils/i18n";

const AppContainer = React.lazy(() => import("./ui/app/app-container"));
const Meeting = React.lazy(() => import("./ui/meeting"));

// Wrapper components for routes that need parameters
const PowConfirmWrapper = () => {
    const { token } = useParams<{ token: string }>();
    return <PowConfirm match={{ params: { token } }} />;
};

const FeedbackWrapper = () => {
    return (
        <Feedback
            onClose={() => {}}
            onPopupClose={() => {}}
            showEmail={false}
        />
    );
};

const SignUpWrapper = () => {
    const { partnerID, programID } = useParams<{
        partnerID: string;
        programID?: string;
    }>();
    const location = useLocation();
    return (
        <SignUp
            match={{ params: { partnerID, programID } }}
            location={location}
        />
    );
};

const store = createStore(rootReducer, applyMiddleware(thunk));
const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            retry: import.meta.env.VITE_APP_ENABLE_QUERY_RETRY !== "false",
        },
    },
});

// Fetch region before app renders (example: useEffect in App)
export function App() {
    return (
        <QueryClientProvider client={queryClient}>
            <Provider store={store}>
                <BrandingContextProvider>
                    <ToastContainer transition={Slide} autoClose={1500} />
                    <React.Suspense fallback={<LoadingContainer />}>
                        {/* @ts-ignore */}
                        <BrowserRouter>
                            <Routes>
                                <Route path="/" element={<Home />} />
                                <Route
                                    path="/india-payment"
                                    element={<IndiaPaymentPage />}
                                />
                                <Route
                                    path="/coach_meet/:meetingId/:token"
                                    element={<CoachMeeting />}
                                />
                                <Route
                                    path="/meet/rejoin"
                                    element={<RejoinPage />}
                                />
                                <Route
                                    path="/meet/error"
                                    element={<MeetingErrorPage />}
                                />
                                <Route
                                    path="/meet/recording"
                                    element={<RecordingPage />}
                                />
                                <Route
                                    path="/meet/:meetingId/:token?"
                                    element={<Meeting />}
                                />
                                <Route
                                    path="/forgot-password"
                                    element={<ForgotPassword />}
                                />
                                <Route
                                    path="/password_resets/:token"
                                    element={<PasswordReset />}
                                />
                                <Route path="/solera" element={<Solera />} />
                                <Route
                                    path="/solera/callback"
                                    element={<SoleraCallback />}
                                />
                                <Route
                                    path="/careplan/register/:token"
                                    element={<CarePlanRegister />}
                                />
                                <Route
                                    path="/dpp-sign-up/:data?"
                                    element={<SignUpContainer />}
                                />
                                <Route
                                    path="/sign-up/:partnerID/:programID?"
                                    element={<SignUpWrapper />}
                                />
                                <Route
                                    path="/jotform/signup"
                                    element={<JotformSignUp />}
                                />
                                <Route path="/pow" element={<PowRegister />} />
                                <Route
                                    path="/pow-success"
                                    element={<PowSuccess />}
                                />
                                <Route
                                    path="/pow-confirm/:token"
                                    element={<PowConfirmWrapper />}
                                />
                                <Route
                                    path="/custom-recipes/:id"
                                    element={<RecipeDetailViewCard />}
                                />
                                <Route
                                    path="/feedbacks"
                                    element={<FeedbackWrapper />}
                                />
                                <Route
                                    path="/new-feedbacks"
                                    element={<NewFeedback />}
                                />
                                <Route
                                    path="/privacy-policy"
                                    element={<Privacy />}
                                />
                                <Route
                                    path="/terms-of-use"
                                    element={<Terms />}
                                />
                                <Route
                                    path="/registered"
                                    element={<Registered />}
                                />
                                <Route
                                    path="/user-signup"
                                    element={<UserSignupComponent />}
                                />
                                <Route
                                    path="/enrollment"
                                    element={<ProjectPowerEnrollment />}
                                />
                                <Route
                                    path="/ohio-university/callback"
                                    element={
                                        <LoginWithOhioUniversityCallback />
                                    }
                                />
                                <Route
                                    path="/mercy/callback"
                                    element={<LoginWithMercyCallback />}
                                />
                                <Route
                                    path="/trinity/callback"
                                    element={<LoginWithTrinityCallback />}
                                />
                                <Route
                                    path="/login"
                                    element={<ExternalLoginHandler />}
                                />
                                <Route
                                    path="/forms/:id"
                                    element={<JotForm />}
                                />
                                <Route
                                    path="/enroll-user/:token"
                                    element={<EnrollUser />}
                                />
                                <Route
                                    path="/subscription"
                                    element={<Subscription />}
                                />
                                <Route
                                    path="/app/*"
                                    element={
                                        <PrivateRoute redirectTo="/">
                                            <AppContainer />
                                        </PrivateRoute>
                                    }
                                />
                                <Route
                                    path="/errors/:type"
                                    element={<ErrorPage />}
                                />
                                <Route path="*" element={<NoMatch />} />
                            </Routes>
                        </BrowserRouter>
                    </React.Suspense>
                </BrandingContextProvider>
            </Provider>
        </QueryClientProvider>
    );
}
